import { useState, type FormEvent } from 'react'
import { Siren } from 'lucide-react'
import { Button, FORM_INPUT_CLASS, Field, FormError, Modal, Switch, TIER_LABELS, WORKER_ROLE_LABELS, type WorkerRole } from '../../core/index.js'
import { schedulingRulesOf, useFacilityConfig } from '../../facilities/index.js'
import { useWorkerRoster } from '../../roster/index.js'
import { useBridgePoolPreview, useOpenBridgeCallout, type BridgePoolRequest } from '../hooks.js'
import type { BridgeCallout } from '../types.js'
import { HELD_BACK_REASON, describeHours } from './bridgeCalloutText.js'

const FORM_ID = 'open-callout-form'
const ROLES: readonly WorkerRole[] = ['CNA', 'LPN', 'RN']
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

interface OpenCalloutModalProps {
  facilityId: string
  onClose: () => void
  onOpened?: (callout: BridgeCallout) => void
  /** The shift to cover, where the callout is opened from a gap already known (a locked seat on the Today tab). */
  initial?: Partial<Pick<CalloutForm, 'date' | 'start' | 'end' | 'unitId' | 'role' | 'absentWorkerId' | 'reason'>>
  /** The open seat on the board the callout is for, where it is opened from one: the seat then shows the callout and is filled by whoever takes it. */
  postingId?: string
}

interface CalloutForm {
  date: string
  start: string
  end: string
  unitId: string
  role: WorkerRole
  absentWorkerId: string
  reason: string
  rate: string
}

const EMPTY: CalloutForm = { date: '', start: '07:00', end: '15:00', unitId: '', role: 'CNA', absentWorkerId: '', reason: '', rate: '' }

/**
 * A facility administrator says a shift needs covering (plan section 7):
 * the shift, who called out and why, the pay to show if any; then the pool
 * the callout would go to, read as the fields are filled, with the hours the
 * platform knows beside each name, anyone held back for overtime and why,
 * and a Tell switch per name - on for everyone who will be told, off for
 * anyone the administrator holds back by hand (a switch that read as Hold
 * back was turned on to include someone, 15 October 2026). Opening tells
 * everyone whose switch is on at once.
 */
export function OpenCalloutModal({ facilityId, onClose, onOpened, initial, postingId }: OpenCalloutModalProps) {
  const [form, setForm] = useState<CalloutForm>({ ...EMPTY, ...initial })
  const [heldBack, setHeldBack] = useState<readonly string[]>([])
  const config = useFacilityConfig(facilityId)
  const roster = useWorkerRoster(facilityId)
  const open = useOpenBridgeCallout(facilityId)

  const namesShift = DATE_PATTERN.test(form.date) && TIME_PATTERN.test(form.start) && TIME_PATTERN.test(form.end)
  const request: BridgePoolRequest | null = namesShift ? { date: form.date, start: form.start, end: form.end, role: form.role, absentWorkerId: form.absentWorkerId || null, holdBack: heldBack } : null
  const preview = useBridgePoolPreview(facilityId, request)
  // A facility that lists its units offers them by name; one that lists none still takes a unit as written.
  const units = config.data ? schedulingRulesOf(config.data).units : []
  const premium = config.data?.clinicalBridge.payPremiumEnabled ? (config.data.clinicalBridge.payPremiumAmount ?? null) : null
  const canOpen = namesShift && form.unitId.trim() !== '' && !open.isPending

  function update(changes: Partial<CalloutForm>): void {
    setForm((current) => ({ ...current, ...changes }))
  }

  function toggleHeldBack(workerId: string, held: boolean): void {
    setHeldBack((current) => (held ? [...current, workerId] : current.filter((id) => id !== workerId)))
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!canOpen) {
      return
    }
    const rate = form.rate.trim() === '' ? null : Number(form.rate)
    const callout = await open.mutateAsync({
      shift: { date: form.date, start: form.start, end: form.end, unitId: form.unitId.trim(), role: form.role },
      ...(form.absentWorkerId ? { absentWorkerId: form.absentWorkerId } : {}),
      ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
      ...(rate !== null && Number.isFinite(rate) ? { pay: { rate, premiumPerHour: premium } } : {}),
      ...(heldBack.length > 0 ? { holdBack: [...heldBack] } : {}),
      ...(postingId ? { postingId } : {}),
    })
    onOpened?.(callout)
    onClose()
  }

  // Someone switched off by hand is listed under the pool as held back, so the preview's own held-back list stays theirs to see.
  const pool = preview.data?.pool ?? []
  const held = preview.data?.heldBack ?? []

  return (
    <Modal
      eyebrow="Clinical Bridge"
      title="Open a callout"
      subtitle="Everyone enrolled for the day who can cover the shift is told at once; the first to accept gets it."
      canClose={!open.isPending}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" form={FORM_ID} icon={Siren} disabled={!canOpen}>
            {open.isPending ? 'Opening…' : pool.length === 0 ? 'Open with nobody to tell' : `Open and tell ${pool.length}`}
          </Button>
          <button type="button" onClick={onClose} disabled={open.isPending} className="text-xs font-bold text-ink-mid hover:text-ink">
            Cancel
          </button>
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-5 p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Day" htmlFor="callout-date" required>
            <input id="callout-date" type="date" value={form.date} onChange={(event) => update({ date: event.target.value })} className={FORM_INPUT_CLASS} required />
          </Field>
          <Field label="Role to cover" htmlFor="callout-role" required>
            <select id="callout-role" value={form.role} onChange={(event) => update({ role: event.target.value as WorkerRole })} className={FORM_INPUT_CLASS}>
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role} · {WORKER_ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Starts" htmlFor="callout-start" required hint="In the facility's zone">
            <input id="callout-start" type="time" value={form.start} onChange={(event) => update({ start: event.target.value })} className={FORM_INPUT_CLASS} required />
          </Field>
          <Field label="Ends" htmlFor="callout-end" required hint="At or before the start means the next morning">
            <input id="callout-end" type="time" value={form.end} onChange={(event) => update({ end: event.target.value })} className={FORM_INPUT_CLASS} required />
          </Field>
          <Field label="Unit" htmlFor="callout-unit" required>
            {units.length > 0 ? (
              <select id="callout-unit" value={form.unitId} onChange={(event) => update({ unitId: event.target.value })} className={FORM_INPUT_CLASS} required>
                <option value="">Choose a unit</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </select>
            ) : (
              <input id="callout-unit" type="text" value={form.unitId} onChange={(event) => update({ unitId: event.target.value })} className={FORM_INPUT_CLASS} placeholder="2-South" maxLength={80} required />
            )}
          </Field>
          <Field label="Who called out" htmlFor="callout-absent" hint="Never in the pool for their own shift">
            <select id="callout-absent" value={form.absentWorkerId} onChange={(event) => update({ absentWorkerId: event.target.value })} className={FORM_INPUT_CLASS}>
              <option value="">Nobody in particular</option>
              {(roster.data ?? []).map((worker) => (
                <option key={worker.workerId} value={worker.workerId}>
                  {worker.name} · {worker.role}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reason" htmlFor="callout-reason">
            <input id="callout-reason" type="text" value={form.reason} onChange={(event) => update({ reason: event.target.value })} className={FORM_INPUT_CLASS} placeholder="Illness" maxLength={200} />
          </Field>
          <Field label="Rate to show" htmlFor="callout-rate" hint={premium !== null ? `Plus the facility's ${premium.toFixed(2)}/hr Bridge premium; blank shows no pay` : 'Per hour; blank shows no pay'}>
            <input id="callout-rate" type="number" min={0} step="0.01" value={form.rate} onChange={(event) => update({ rate: event.target.value })} className={FORM_INPUT_CLASS} placeholder="20.00" />
          </Field>
        </div>

        <section aria-label="Who would be told">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-marine">Who would be told</div>
          {!namesShift ? (
            <p className="text-xs text-ink-mid">Name the day and times to see who is enrolled for it.</p>
          ) : preview.isPending ? (
            <p className="text-xs text-ink-mid">Looking…</p>
          ) : preview.isError ? (
            <FormError error={preview.error} fallback="The pool could not be read." />
          ) : (
            <div className="flex flex-col gap-3 text-xs">
              {pool.length === 0 && held.length === 0 && <p className="text-ink-mid">Nobody eligible is enrolled for that day and role: the pool is everyone enrolled for the day, made eligible by staff, of the role the shift needs and still at the facility. You can still open the callout; nobody will be told.</p>}
              {(pool.length > 0 || held.length > 0) && (
                <ul className="divide-y divide-marine/10 rounded border border-marine/15 bg-white">
                  {pool.map((member) => (
                    <li key={member.workerId} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span>
                        <span className="font-bold text-ink">{member.name}</span> <span className="text-ink-mid">· {TIER_LABELS[member.tier]} · {describeHours(member.hoursThisWeek)} · will be told</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2 text-[11px] font-bold text-ink-mid">
                        Tell
                        <Switch checked onChange={() => toggleHeldBack(member.workerId, true)} label={`Tell ${member.name}`} />
                      </span>
                    </li>
                  ))}
                  {held.map((entry) => (
                    <li key={entry.workerId} className="flex items-center justify-between gap-3 px-3 py-2 text-ink-mid">
                      <span>
                        {entry.name} · {describeHours(entry.hoursThisWeek)} · <span className="font-bold">{HELD_BACK_REASON[entry.reason]}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2 text-[11px] font-bold text-ink-mid">
                        Tell
                        <Switch checked={false} disabled={entry.reason !== 'by_administrator'} onChange={() => toggleHeldBack(entry.workerId, false)} label={`Tell ${entry.name}`} />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {preview.data && <p className="text-ink-mid">The shift adds {preview.data.shiftHours} hours to a week; anyone it would take past 40 is held back unless the facility permits overtime.</p>}
            </div>
          )}
        </section>

        {open.isError && <FormError error={open.error} fallback="The callout could not be opened." />}
      </form>
    </Modal>
  )
}
